import type { SavedView, SaveViewPayload, SaveViewResult, ViewType } from "./DataTable.types";

type ApiErrorEnvelope = {
  success: false;
  status_code: number;
  message: string;
  error: Record<string, string>;
};

type ApiAuthError = {
  detail: { code: string; message: string };
};

export type SaveViewHeaderSource =
  | Record<string, string | null | undefined>
  | (() => Record<string, string | null | undefined>);

export type SaveViewTokenSource = string | null | undefined | (() => string | null | undefined);

export type SaveViewApiConfig = {
  /**
   * Base URL of the API service. Both host roots and `/api/v1` roots are
   * supported, e.g. `https://platform.shiprocket.in` or
   * `https://platform.shiprocket.in/api/v1/`.
   */
  baseUrl?: string;
  /**
   * Back-compat endpoint override for direct saves. Prefer `saveEndpoint` for
   * new code. Component-owned saved-view flows only POST when `endpoint`,
   * `saveEndpoint`, or `remoteSave` is supplied explicitly.
   */
  endpoint?: string;
  /** Optional create/save endpoint. Omit when save should remain localStorage-only. */
  saveEndpoint?: string;
  listEndpoint?: string;
  getEndpoint?: string;
  updateEndpoint?: string;
  deleteEndpoint?: string;
  /** Backend view_type. Defaults to the save payload's view_type. */
  viewType?: ViewType;
  /** Backend tab scope, e.g. "all" or "new". Added to query/body when present. */
  tab?: string;
  /** Auth token or lazy token reader. Defaults to common Shiprocket localStorage keys. */
  token?: SaveViewTokenSource;
  /** Extra headers or lazy header reader. */
  headers?: SaveViewHeaderSource;
  credentials?: RequestCredentials;
  fetcher?: typeof globalThis.fetch;
};

export type SaveViewOptions = SaveViewApiConfig & {
  payload: SaveViewPayload;
};

const DEFAULT_SAVE_ENDPOINT = "api/v1/saveview";
const DEFAULT_LIST_ENDPOINT = "api/v1/listviews";
const DEFAULT_GET_ENDPOINT = "api/v1/getview";
const DEFAULT_UPDATE_ENDPOINT = "api/v1/updateview";
const DEFAULT_DELETE_ENDPOINT = "api/v1/deleteview";

function readLocalStorageValue(key: string): string {
  try {
    if (typeof window === "undefined" || !window.localStorage) return "";
    return window.localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function resolveToken(token: SaveViewTokenSource): string {
  const value = typeof token === "function" ? token() : token;
  if (value !== undefined && value !== null) return value;
  return readLocalStorageValue("satellizer_token") || readLocalStorageValue("token");
}

function formatBearerToken(token: string) {
  return token.toLowerCase().startsWith("bearer ") ? token : `Bearer ${token}`;
}

function resolveHeaders(headers: SaveViewHeaderSource | undefined) {
  return typeof headers === "function" ? headers() : headers;
}

function buildSaveViewUrl(baseUrl = "", endpoint = DEFAULT_SAVE_ENDPOINT, query?: Record<string, string | null | undefined>): string {
  const cleanEndpoint = endpoint.replace(/^\/+/, "");
  const cleanBase = baseUrl.replace(/\/+$/, "");
  const params = new URLSearchParams();

  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, value);
  });

  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  if (!cleanBase) return `/${cleanEndpoint}${suffix}`;

  if (/\/api\/v1$/i.test(cleanBase) && /^api\/v1(?:\/|$)/i.test(cleanEndpoint)) {
    return `${cleanBase}/${cleanEndpoint.replace(/^api\/v1\/?/i, "")}${suffix}`;
  }

  return `${cleanBase}/${cleanEndpoint}${suffix}`;
}

function buildSaveViewHeaders({ token, headers }: Pick<SaveViewApiConfig, "token" | "headers">): Record<string, string> {
  const resolvedToken = resolveToken(token);
  const cookiie = readLocalStorageValue("cookiie");
  const merged: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "No-Auth": "True",
  };

  if (resolvedToken) merged.Authorization = formatBearerToken(resolvedToken);
  if (cookiie) merged.Cookiie = cookiie;

  const extraHeaders = resolveHeaders(headers);
  Object.entries(extraHeaders ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) merged[key] = value;
  });

  return merged;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function dataOf(body: unknown): unknown {
  return isRecord(body) && "data" in body ? body.data : body;
}

function normalizeBoolean(value: unknown): boolean {
  return value === true || value === "true" || value === 1 || value === "1";
}

function normalizeSavedView(value: unknown): SavedView | null {
  if (!isRecord(value)) return null;
  const viewId = value.view_id ?? value.viewId ?? value.id;
  const name = value.name ?? value.view_name ?? value.viewName;
  const viewType = value.view_type ?? value.viewType;

  if (viewId === undefined && name === undefined) return null;

  return {
    view_id: String(viewId ?? ""),
    name: String(name ?? ""),
    view_type: String(viewType ?? ""),
    tab: value.tab === undefined || value.tab === null ? undefined : String(value.tab),
    columns: Array.isArray(value.columns) ? (value.columns as SavedView["columns"]) : [],
    filters: Array.isArray(value.filters) ? (value.filters as SavedView["filters"]) : [],
    priority: Number(value.priority ?? 0),
    is_default: normalizeBoolean(value.is_default ?? value.isDefault ?? value.default ?? value.primary),
    is_system_defined: normalizeBoolean(value.is_system_defined ?? value.isSystemDefined ?? value.system_defined ?? value.systemDefined),
  };
}

function extractViews(body: unknown): SavedView[] {
  const data = dataOf(body);
  const candidates = [
    data,
    isRecord(data) ? data.views : undefined,
    isRecord(data) ? data.saved_views : undefined,
    isRecord(data) ? data.savedViews : undefined,
    isRecord(data) ? data.items : undefined,
    isRecord(data) ? data.records : undefined,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.map(normalizeSavedView).filter((view): view is SavedView => Boolean(view));
    }
  }

  const view = extractView(body);
  return view ? [view] : [];
}

function extractView(body: unknown): SavedView | null {
  const data = dataOf(body);
  const candidates = [
    isRecord(data) ? data.view : undefined,
    isRecord(data) ? data.saved_view : undefined,
    isRecord(data) ? data.savedView : undefined,
    isRecord(data) ? data.primary : undefined,
    data,
  ];

  for (const candidate of candidates) {
    const view = normalizeSavedView(candidate);
    if (view) return view;
  }

  return null;
}

function extractAlreadyExists(body: unknown): boolean {
  const data = dataOf(body);
  if (!isRecord(data)) return false;
  return normalizeBoolean(data.already_exists ?? data.alreadyExists);
}

function scopedPayload(payload: SaveViewPayload, config: Pick<SaveViewApiConfig, "viewType" | "tab">): SaveViewPayload {
  return {
    ...payload,
    view_type: config.viewType ?? payload.view_type,
    ...(config.tab ? { tab: config.tab } : {}),
  };
}

function scopedQuery(config: Pick<SaveViewApiConfig, "viewType" | "tab">, payload?: Pick<SaveViewPayload, "view_type">) {
  return {
    view_type: String(config.viewType ?? payload?.view_type ?? ""),
    tab: config.tab,
  };
}

async function readResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") || "";
  return contentType.includes("application/json") ? ((await response.json()) as unknown) : ((await response.text()) as unknown);
}

async function requestViewApi(
  config: SaveViewApiConfig,
  request: { method: "GET" | "POST" | "PUT" | "DELETE"; endpoint: string; query?: Record<string, string | null | undefined>; body?: unknown }
): Promise<unknown> {
  const resolvedFetch = config.fetcher ?? globalThis.fetch;
  if (!resolvedFetch) throw new Error("Fetch is not available in this environment.");

  const response = await resolvedFetch(buildSaveViewUrl(config.baseUrl, request.endpoint, request.query), {
    method: request.method,
    headers: buildSaveViewHeaders({ token: config.token, headers: config.headers }),
    credentials: config.credentials,
    ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
  });

  const body = await readResponseBody(response);

  // Auth errors use FastAPI's native shape — not the envelope
  if (response.status === 401) {
    const err = body as ApiAuthError;
    throw new Error(err.detail?.message ?? "Unauthorized");
  }

  if (!response.ok) {
    const err = body as ApiErrorEnvelope;
    throw new Error(err.message ?? `Request failed: ${response.status}`);
  }

  return body;
}

/**
 * POST /api/v1/saveview
 *
 * Saves a user view. Returns the saved view and whether it was a duplicate.
 * Throws on HTTP error (auth, validation, network).
 */
export async function saveView({
  baseUrl,
  endpoint,
  saveEndpoint,
  viewType,
  tab,
  token,
  headers,
  credentials,
  fetcher,
  payload,
}: SaveViewOptions): Promise<SaveViewResult> {
  const body = await requestViewApi(
    { baseUrl, token, headers, credentials, fetcher },
    {
      method: "POST",
      endpoint: saveEndpoint ?? endpoint ?? DEFAULT_SAVE_ENDPOINT,
      body: scopedPayload(payload, { viewType, tab }),
    }
  );
  const view = extractView(body);
  if (!view) throw new Error("Save view response did not include a view.");

  return {
    view,
    alreadyExists: extractAlreadyExists(body),
  };
}

export async function listViews(config: SaveViewApiConfig): Promise<SavedView[]> {
  const body = await requestViewApi(config, {
    method: "GET",
    endpoint: config.listEndpoint ?? DEFAULT_LIST_ENDPOINT,
    query: scopedQuery(config),
  });
  return extractViews(body);
}

export async function getView(config: SaveViewApiConfig): Promise<SavedView | null> {
  const body = await requestViewApi(config, {
    method: "GET",
    endpoint: config.getEndpoint ?? DEFAULT_GET_ENDPOINT,
    query: scopedQuery(config),
  });
  return extractView(body);
}

export async function updateView(config: SaveViewApiConfig & { payload: SaveViewPayload }): Promise<SavedView | null> {
  const body = await requestViewApi(config, {
    method: "PUT",
    endpoint: config.updateEndpoint ?? DEFAULT_UPDATE_ENDPOINT,
    body: scopedPayload(config.payload, config),
  });
  return extractView(body);
}

export async function deleteView(config: SaveViewApiConfig): Promise<void> {
  await requestViewApi(config, {
    method: "DELETE",
    endpoint: config.deleteEndpoint ?? DEFAULT_DELETE_ENDPOINT,
    query: scopedQuery(config),
  });
}

/**
 * Creates a pre-configured save function bound to a base URL and token.
 * The returned function matches the `TableSaveView.onSave` signature directly.
 */
export function createSaveView(config: SaveViewApiConfig = {}) {
  return (payload: SaveViewPayload) => saveView({ ...config, payload });
}
